import { storeToRefs } from 'pinia';

export type UnpaidInvoicesSource = 'banner' | 'sidebar_badge' | 'blocked_redirect' | 'email' | 'direct';

/**
 * PostHog tracking of the unpaid funnel (TDD-6833): banners and level 4 block,
 * Mes factures, transfer modal. Impersonation is filtered by the tracker itself
 * and the tenant group + unpaid level ride on every event (PostHogTrackerService).
 * A forced test level (useUnpaidDevForce) sends nothing. Never pass a bank detail
 * value (IBAN, communication, copied text): only field names.
 */
export const useUnpaidTracking = () => {
    const { $tracker } = useNuxtApp();
    const { authed } = storeToRefs(useAuthStore());
    const { level, invoicesCount, totalDue } = useUnpaidStatus();
    // Entry point of the next Mes factures visit, set by whatever leads there.
    const invoicesSource = useState<UnpaidInvoicesSource | null>('unpaid-invoices-source', () => null);

    const isForced = () => getForcedUnpaidLevel() !== null;

    const track = (event: string, properties: Record<string, unknown> = {}) => {
        if (isForced()) return;
        $tracker.track(event, { user_role: authed.value?.role ?? null, ...properties });
    };

    // Exposure snapshot shared by the banner and the block (amounts in euros, the app prices in cents).
    const exposure = () => ({ unpaid_level: level.value, invoices_count: invoicesCount.value, total_due: totalDue.value / 100 });

    // Once per browser session and per level.
    const trackBannerViewed = () => {
        if (isForced()) return;
        const key = `unpaid_banner_viewed:${level.value}`;
        try {
            if (sessionStorage.getItem(key)) return;
            sessionStorage.setItem(key, '1');
        } catch {
            // Storage unavailable: track anyway, duplicates beat a missing exposure.
        }
        track('unpaid_banner_viewed', exposure());
    };

    const setInvoicesSource = (source: UnpaidInvoicesSource) => {
        invoicesSource.value = source;
    };

    // `?source=email` comes from the level change emails (TDD-6548).
    const trackInvoicesPageViewed = () => {
        const source = invoicesSource.value ?? (useRoute().query.source === 'email' ? 'email' : 'direct');
        invoicesSource.value = null;

        if (source === 'blocked_redirect') track('unpaid_blocked_screen_viewed', exposure());
        track('unpaid_invoices_page_viewed', { unpaid_level: level.value, source });
    };

    return {
        track,
        trackBannerViewed,
        setInvoicesSource,
        trackInvoicesPageViewed,
    };
};
